import { cx } from "./ui.js";

/**
 * A moldura do artefato — a peça de assinatura do sistema.
 *
 * O produto final é um composto 1080×1920 com a capa do livro nos 40% de cima e
 * o retrato nos 60% de baixo. Esta moldura reproduz essa geometria e vai se
 * preenchendo conforme o operador avança: primeiro a foto embaixo, depois a capa
 * em cima, e no fim ela É o resultado.
 *
 * É o indicador de progresso do fluxo. Um stepper "01 / 02 / 03" seria mentira
 * aqui — o operador pula etapas (nem toda foto leva capa) e volta atrás. O que
 * ele precisa saber não é em que passo está, e sim **o que já tem**. Esta moldura
 * responde isso de relance, no meio de uma conversa com a pessoa fotografada.
 */

const PROPORCAO_CAPA = 0.4;

export type EstadoArtefato = "vazio" | "montando" | "processando" | "pronto" | "falhou";

export interface ArtefatoProps {
  fotoUrl?: string | null;
  capaUrl?: string | null;
  /** Quando existe, substitui as duas faixas: é a peça final composta. */
  compostoUrl?: string | null;
  estado?: EstadoArtefato;
  /** `tira` é a versão compacta que acompanha as etapas de entrada. */
  tamanho?: "tira" | "cheio";
  legenda?: string;
  /** Os rótulos "capa" e "foto" só fazem sentido no contexto de trabalho. */
  rotulos?: boolean;
  className?: string;
}

export function Artefato({
  fotoUrl,
  capaUrl,
  compostoUrl,
  estado = "montando",
  tamanho = "tira",
  legenda,
  rotulos = true,
  className,
}: ArtefatoProps) {
  const compacto = tamanho === "tira";

  return (
    <figure
      className={cx(
        "flex items-center gap-3",
        // Em modo cheio a figura precisa de largura própria: sem isso o `w-full`
        // da moldura interna resolve para zero e a peça some.
        compacto ? "flex-row" : "w-full flex-col",
        className,
      )}
    >
      <div
        className={cx(
          "relative shrink-0 overflow-hidden rounded-peca bg-black",
          "ring-1 ring-borda",
          compacto ? "h-16" : "w-full max-w-[17rem]",
          estado === "processando" && "ring-projecao/60",
          estado === "pronto" && "ring-fixado/50",
          estado === "falhou" && "ring-descartar/60",
        )}
        style={{ aspectRatio: "9 / 16" }}
        aria-label={descreverEstado(estado, Boolean(fotoUrl), Boolean(capaUrl))}
      >
        {compostoUrl ? (
          <img
            src={compostoUrl}
            alt="Peça final composta"
            className="absolute inset-0 size-full object-cover"
          />
        ) : (
          <>
            <Faixa
              url={capaUrl}
              rotulo="capa"
              className="absolute inset-x-0 top-0"
              altura={`${PROPORCAO_CAPA * 100}%`}
              mostrarRotulo={rotulos && !compacto}
            />
            <Faixa
              url={fotoUrl}
              rotulo="foto"
              className="absolute inset-x-0 bottom-0"
              altura={`${(1 - PROPORCAO_CAPA) * 100}%`}
              mostrarRotulo={rotulos && !compacto}
            />
            {/* A linha de corte 40/60 fica visível enquanto a peça não está
                montada: é a régua que o operador está preenchendo. */}
            <div
              aria-hidden
              className="absolute inset-x-0 h-px bg-borda-forte/70"
              style={{ top: `${PROPORCAO_CAPA * 100}%` }}
            />
          </>
        )}

        {estado === "processando" && (
          <div
            aria-hidden
            className="revelando absolute inset-0 bg-gradient-to-t from-projecao/45 to-transparent"
          />
        )}
      </div>

      {legenda && (
        <figcaption
          className={cx(
            "text-[0.8125rem] leading-snug text-texto-suave",
            compacto ? "flex-1" : "text-center",
          )}
        >
          {legenda}
        </figcaption>
      )}
    </figure>
  );
}

function Faixa({
  url,
  rotulo,
  altura,
  className,
  mostrarRotulo,
}: {
  url?: string | null;
  rotulo: string;
  altura: string;
  className?: string;
  mostrarRotulo: boolean;
}) {
  return (
    <div className={cx("overflow-hidden", className)} style={{ height: altura }}>
      {url ? (
        <img src={url} alt="" className="size-full object-cover" />
      ) : (
        <div className="flex size-full items-center justify-center bg-superficie/60">
          {mostrarRotulo && (
            <span className="text-[0.6875rem] uppercase tracking-[0.14em] text-texto-fraco">
              {rotulo}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function descreverEstado(estado: EstadoArtefato, temFoto: boolean, temCapa: boolean): string {
  if (estado === "processando") return "Gerando a imagem";
  if (estado === "falhou") return "A geração falhou";
  if (estado === "pronto") return "Peça pronta";
  if (temFoto && temCapa) return "Foto e capa escolhidas";
  if (temFoto) return "Foto escolhida, falta a capa";
  return "Nenhuma imagem ainda";
}
